import type { Invoice } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { formatMoney, formatQty, whenAt } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';
import { ErrorBox } from './ui';

/**
 * Shorts on an issued invoice (decision 95): type what each line really delivered (lower than it says), with a note;
 * the totals, the stock and the order follow, and every change is listed below with who made it.
 */
export function ShortsPanel({ invoice }: { invoice: Invoice }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState<Record<string, string>>({});
  const [note, setNote] = useState('');
  const [confirmCredits, setConfirmCredits] = useState(false);
  const history = useQuery({ queryKey: qk.invoiceAdjustments(invoice.id), queryFn: ({ signal }) => api.invoiceAdjustments(invoice.id, signal) });
  // a credit line (decision 188) is not delivered: it is changed with Edit, never shorted
  const goods = invoice.lines.filter((l) => !l.creditReason);
  const start = () => {
    setQty(Object.fromEntries(goods.map((l) => [l.id, String(l.qty)])));
    setNote('');
    setConfirmCredits(false);
    save.reset();
    setOpen(true);
  };
  const parsed = goods.map((l) => {
    const raw = (qty[l.id] ?? String(l.qty)).trim();
    const n = raw === '' ? NaN : Number(raw);
    const bad = !Number.isFinite(n) || n < 0 || n > l.qty;
    return { line: l, n, bad, changed: !bad && n !== l.qty };
  });
  const changes = parsed.filter((p) => p.changed);
  const anyBad = parsed.some((p) => p.bad);
  // as the server does it: the printed lines summed, the tax at the rate the invoice was issued with
  const cents = (v: number) => Math.round(v * 100) / 100;
  const newSubtotal = cents(invoice.subtotal - changes.reduce((a, p) => a + (p.line.amount - cents(p.n * p.line.unitPrice)), 0));
  const rate = invoice.taxRate ?? (invoice.subtotal > 0 ? invoice.tax / invoice.subtotal : 0);
  const newTotal = cents(newSubtotal + cents(newSubtotal * rate));
  const save = useMutation({
    mutationFn: () =>
      api.invoiceShorts(invoice.id, { lines: changes.map((p) => ({ lineId: p.line.id, qty: p.n })), ...(note.trim() ? { note: note.trim() } : {}), ...(confirmCredits ? { confirmCredits: true } : {}) }),
    onSuccess: (inv) => {
      qc.setQueryData(qk.invoice(invoice.id), inv);
      setOpen(false);
      setConfirmCredits(false);
      void qc.invalidateQueries({ queryKey: qk.invoiceAdjustments(invoice.id) });
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['customers'] });
      void qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });

  return (
    <section className="card stack-sm no-print" data-testid="shorts-panel">
      <h2>{t('invShortsTitle')}</h2>
      {open ? (
        <form
          className="stack-sm"
          onSubmit={(e) => {
            e.preventDefault();
            if (changes.length && !anyBad) save.mutate();
          }}
        >
          <p className="muted">{t('invShortsHint')}</p>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('itemCol')}</th>
                  <th className="al-right">{t('shortsOnInvoice')}</th>
                  <th className="al-right">{t('shortsDelivered')}</th>
                </tr>
              </thead>
              <tbody>
                {parsed.map(({ line, bad }) => (
                  <tr key={line.id}>
                    <td>{line.description}</td>
                    <td className="al-right">
                      {formatQty(line.qty)} {line.unit ?? ''}
                    </td>
                    <td className="al-right">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="qty-input"
                        aria-label={`${t('shortsDelivered')}: ${line.description}`}
                        aria-invalid={bad || undefined}
                        value={qty[line.id] ?? ''}
                        onChange={(e) => setQty((cur) => ({ ...cur, [line.id]: e.target.value }))}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {anyBad ? <p className="field-error">{t('shortsBad')}</p> : null}
          <label className="field">
            <span>{t('shortsNote')}</span>
            <input type="text" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('shortsNotePlaceholder')} />
          </label>
          {changes.length && !anyBad ? (
            <p className="note" role="status">
              {t('shortsNewTotal').replace('{n}', String(changes.length)).replace('{total}', formatMoney(newTotal, intlLocale))}
            </p>
          ) : null}
          {save.error instanceof ApiError && save.error.problem.errors?.some((e) => e.path === 'confirmCredits') ? (
            <>
              <p className="flash" role="alert">
                {save.error.message}
              </p>
              <label className="check">
                <input type="checkbox" checked={confirmCredits} onChange={(e) => setConfirmCredits(e.target.checked)} /> {t('shortsConfirmCredits')}
              </label>
            </>
          ) : save.error ? (
            <ErrorBox error={save.error} />
          ) : null}
          <div className="row-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setOpen(false)}>
              {t('cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={save.isPending || !changes.length || anyBad}>
              {t('shortsSave')}
            </button>
          </div>
        </form>
      ) : (
        <div className="row-actions">
          <button type="button" className="btn btn-secondary" onClick={start}>
            {t('shortsRecord')}
          </button>
        </div>
      )}
      {history.data?.length ? (
        <ul className="list compact" data-testid="shorts-history">
          {history.data.map((a) => (
            <li key={a.id} className="row">
              <span className="row-main">
                <span className="row-title">
                  {t(`adjKind_${a.kind}`)} · {a.description}: {a.qtyBefore !== a.qtyAfter ? `${formatQty(a.qtyBefore)} → ${formatQty(a.qtyAfter)}` : formatQty(a.qtyAfter)}
                  {a.priceBefore !== null && a.priceAfter !== null && a.priceBefore !== a.priceAfter ? ` @ ${formatMoney(a.priceBefore, intlLocale)} → ${formatMoney(a.priceAfter, intlLocale)}` : ''} ({a.amountAfter > a.amountBefore ? '+' : ''}{formatMoney(a.amountAfter - a.amountBefore, intlLocale)})
                </span>
                <span className="row-sub">
                  {whenAt(a.createdAt, intlLocale)}
                  {a.recordedBy ? ` · ${a.recordedBy}` : ''}
                  {a.note ? ` · ${a.note}` : ''}
                </span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
